import { useState } from 'react';

import type { TDocumentDefinitions } from 'pdfmake/interfaces';

import { IcArrowSmall } from '@/assets/images';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/TailwindComponents';

import {
  PieChartWidget,
  PieChartCardData,
  renderPieChartToBase64,
} from './PieChartWidget';

interface ExportReportModalProps {
  open: boolean;
  onClose: () => void;
  reportData: {
    generatedDate: string;
    filters: string;
    cases: Array<{
      caseId: string;
      caseType: string;
      status: string;
      officer: string;
      createdBy: string;
      createdAt: string;
      lastUpdated: string;
    }>;
    statusData: Array<{
      name: string;
      value: number;
      color: string;
    }>;
  } | null;
}

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr || dateStr === 'N/A') return dateStr;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
};

// Format date for filter display
const formatFilterDate = (dateStr: string): string => {
  if (!dateStr || dateStr === 'All') return dateStr;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
};

// Parse filter string and return individual filter values
const parseFilters = (filterStr: string) => {
  const defaults = {
    dateRange: 'All',
    caseType: 'All',
    status: 'All',
    officer: 'All',
    createdBy: 'All',
  };

  if (!filterStr || filterStr === 'No filters applied') {
    return defaults;
  }

  // Parse "Field: Value, Field2: Value2" format
  const parts = filterStr.split(', ');
  parts.forEach((part) => {
    const [key, ...valueParts] = part.split(': ');
    const value = valueParts.join(': ');
    if (key && value) {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('date') || keyLower.includes('created at')) {
        // Format date range values
        if (value.includes(' - ')) {
          const [start, end] = value.split(' - ');
          defaults.dateRange = `${formatFilterDate(start)} - ${formatFilterDate(
            end,
          )}`;
        } else {
          defaults.dateRange = formatFilterDate(value);
        }
      } else if (keyLower.includes('case type')) {
        defaults.caseType = value;
      } else if (keyLower.includes('status')) {
        defaults.status = value;
      } else if (keyLower.includes('officer')) {
        defaults.officer = value;
      } else if (keyLower.includes('created by')) {
        defaults.createdBy = value;
      }
    }
  });

  return defaults;
};

export const ExportReportModal = ({
  open,
  onClose,
  reportData,
}: ExportReportModalProps) => {
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (!reportData || exporting) return;
    setExporting(true);

    try {
      const pdfMakeModule = await import('pdfmake/build/pdfmake');
      const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
      const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
      const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;
      pdfMake.vfs = pdfFonts.vfs || pdfFonts;

      // Generate pie chart image
      const chartImage = renderPieChartToBase64(pieChartData);

      // Parse filters for individual cells
      const parsedFilters = parseFilters(reportData.filters);

      const tableBody = [
        // Header row with green background
        [
          'Case ID',
          'Case Type',
          'Status',
          'Officer(s)',
          'Created By',
          'Created At',
          'Last Updated',
        ].map((header) => ({
          text: header,
          bold: true,
          fontSize: 7,
          fillColor: '#A9EE8A',
          color: 'black',
          margin: [2, 3, 2, 3] as [number, number, number, number],
        })),
        // Data rows with alternating gray backgrounds
        ...reportData.cases.map((row, rowIndex) =>
          [
            row.caseId,
            row.caseType,
            row.status,
            row.officer,
            row.createdBy,
            formatDisplayDate(row.createdAt),
            formatDisplayDate(row.lastUpdated),
          ].map((value) => ({
            text: value || '',
            fontSize: 6,
            fillColor: rowIndex % 2 === 0 ? '#f5f5f5' : '#e8e8e8',
            margin: [2, 2, 2, 2] as [number, number, number, number],
          })),
        ),
      ];

      const definition: TDocumentDefinitions = {
        pageSize: 'A4',
        pageMargins: [30, 30, 30, 50],
        content: [
          // Title with green background
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: 'Internal Affairs Report',
                    alignment: 'center',
                    bold: true,
                    fontSize: 16,
                    color: 'black',
                    fillColor: '#A9EE8A',
                    margin: [0, 10, 0, 10] as [number, number, number, number],
                  },
                ],
              ],
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 10] as [number, number, number, number],
          },
          // Filters with separate cells
          {
            table: {
              widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*'],
              body: [
                [
                  {
                    text: 'Filters:',
                    bold: true,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Date Range: ${parsedFilters.dateRange}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Case Type: ${parsedFilters.caseType}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Status: ${parsedFilters.status}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Officer: ${parsedFilters.officer}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Created By: ${parsedFilters.createdBy}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                  },
                  {
                    text: `Generated: ${reportData.generatedDate}`,
                    fontSize: 7,
                    fillColor: '#e5e5e5',
                    margin: [4, 4, 4, 4] as [number, number, number, number],
                    alignment: 'right',
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => 'white',
              vLineColor: () => 'white',
            },
            margin: [0, 0, 0, 15] as [number, number, number, number],
          },
          // Pie chart with full-width gray background
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    image: chartImage,
                    width: 300,
                    alignment: 'center',
                    fillColor: '#f0f0f0',
                    margin: [0, 15, 0, 15] as [number, number, number, number],
                  },
                ],
              ],
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 20] as [number, number, number, number],
          },
          {
            table: {
              headerRows: 1,
              widths: ['*', '*', '*', '*', '*', '*', '*'],
              body: tableBody,
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => 'white',
              vLineColor: () => 'white',
            },
          },
        ],
        footer: (_currentPage: number, _pageCount: number) => ({
          columns: [
            {
              text: '911Systems \u00BB',
              alignment: 'center',
              fontSize: 9,
              color: '#999',
              margin: [0, 10, 0, 0] as [number, number, number, number],
            },
          ],
        }),
      };

      const createPdf = pdfMake.createPdf || pdfMake;
      const dateStr = new Date().toISOString().split('T')[0];
      createPdf(definition).download(`IA_Report_${dateStr}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;

    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const headers = [
      'Case ID',
      'Case Type',
      'Status',
      'Officer(s)',
      'Created By',
      'Created At',
      'Last Updated',
    ];
    const rows = reportData.cases.map((row) =>
      [
        row.caseId,
        row.caseType,
        row.status,
        row.officer,
        row.createdBy,
        formatDisplayDate(row.createdAt),
        formatDisplayDate(row.lastUpdated),
      ]
        .map((v) => escapeCSV(v || ''))
        .join(','),
    );

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStr = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `IA_Report_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!reportData) return null;

  // Transform statusData to match PieChartWidget format
  const totalCases = reportData.statusData.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  const pieChartData: PieChartCardData = {
    type: 'pieChart',
    value: totalCases,
    breakdown: reportData.statusData.map((item) => ({
      type: item.name,
      percentage: Math.round((item.value / totalCases) * 100),
      count: item.value,
    })),
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[900px] max-h-[90vh] flex flex-col overflow-hidden [&>button.absolute]:text-red-500">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle>Export report</DialogTitle>
        </DialogHeader>

        {/* Scrollable content area - hidden scrollbar */}
        <div
          className="flex-1 overflow-y-auto min-h-0"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>
            {`
              .flex-1::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          <div className="space-y-6 p-4 border border-gray-200 rounded-lg">
            {/* Report Header */}
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-center bg-[#A9EE8A] py-3 -mx-4 px-4">
                Internal Affairs Report
              </h2>
              {/* Filters with separate cells */}
              <div className="flex flex-wrap items-center text-xs text-gray-700 bg-[#e5e5e5] -mx-4 px-2">
                <span className="px-2 py-2 border-r border-white font-semibold">
                  Filters:
                </span>
                <span className="px-2 py-2 border-r border-white">
                  Date Range: {parseFilters(reportData.filters).dateRange}
                </span>
                <span className="px-2 py-2 border-r border-white">
                  Case Type: {parseFilters(reportData.filters).caseType}
                </span>
                <span className="px-2 py-2 border-r border-white">
                  Status: {parseFilters(reportData.filters).status}
                </span>
                <span className="px-2 py-2 border-r border-white">
                  Officer: {parseFilters(reportData.filters).officer}
                </span>
                <span className="px-2 py-2 border-r border-white">
                  Created By: {parseFilters(reportData.filters).createdBy}
                </span>
                <span className="px-2 py-2 ml-auto">
                  Generated: {reportData.generatedDate}
                </span>
              </div>
            </div>

            {/* PieChart Section with full-width gray background */}
            <div className="flex justify-center bg-[#f0f0f0] py-4 -mx-4 px-4">
              <PieChartWidget data={pieChartData} showTotal={false} />
            </div>

            {/* Table Section - Only this scrolls horizontally - hidden scrollbar */}
            <div
              className="overflow-x-auto -mx-4 px-4"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <style>
                {`
                  .overflow-x-auto::-webkit-scrollbar {
                    display: none;
                  }
                `}
              </style>
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[100px]">
                      Case ID
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[120px]">
                      Case Type
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[100px]">
                      Status
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[110px]">
                      Officer(s)
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[90px]">
                      Created By
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[130px]">
                      Created At
                    </TableHead>
                    <TableHead className="border border-white bg-[#A9EE8A] text-black font-semibold text-xs p-2 whitespace-nowrap min-w-[130px]">
                      Last Updated
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.cases.map((row, rowIndex) => (
                    <TableRow
                      key={row.caseId}
                      className={
                        rowIndex % 2 === 0 ? 'bg-[#f5f5f5]' : 'bg-[#e8e8e8]'
                      }
                    >
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {row.caseId}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {row.caseType}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {row.status}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {row.officer}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {row.createdBy}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {formatDisplayDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="border border-white text-black text-xs p-2 whitespace-nowrap">
                        {formatDisplayDate(row.lastUpdated)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0 z-50">
          <Button
            type="button"
            className="rounded-md bg-white"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                className="rounded-md"
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : 'Actions'}
                {!exporting && <IcArrowSmall />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="">
              <DropdownMenuItem
                onClick={handleExportPDF}
                className="font-semibold"
              >
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExportCSV}
                className="font-semibold"
              >
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportReportModal;
